import type { ComputedRef } from 'vue'
import { computed, inject, provide, reactive, toRef } from 'vue'
import { bridge, frame, len, logo, model, navConfigurations, randomConfig } from '@/constants/state'
import { find, groupBy, sample, shuffle, toArray, transformFrame, transformLensColor, transformLensOption, transformModel, transformTemple } from '@/transforms/object'
import { modelTypes } from '@/hooks/enums'

export const initCustomization = () => {
  //* State
  const state = reactive({
    masterData: {} as DefinitionsType,
    currentNav: modelTypes.FRAME as string,
    defaultVariant: null as (InputVariantType | null),
    demoMode: false,
    // TODO: 2 attribute for experiments memories leak
    isShowSwitchMode: false,
    isForceRefresh: false,
    previousRandomKeys: [] as string[],
  })

  // ? Getters
  const getNavConfigurations = computed(() => navConfigurations[state.currentNav])
  const getModelOptions = computed(() => toArray(model.modelOptions))
  const getAllModels = computed(() => (state.masterData?.modelName ?? []).map(transformModel))

  const onSaleModels = computed(() => getAllModels.value.filter(item => item.isOnSale).sort((a, b) => a.sorter - b.sorter))
  const onForChildModels = computed(() => getAllModels.value.filter(item => item.isForChild).sort((a, b) => a.sorter - b.sorter))
  const onDevModels = computed(() => getAllModels.value.filter(item => item.isOnDev).sort((a, b) => a.sorter - b.sorter))

  const getDisplayModels = computed(() => [...onSaleModels.value, ...onForChildModels.value, ...onDevModels.value])
  const getFrameOptions = computed(() => toArray(frame.frameOptions))
  const getFrameItems = computed(() => (state.masterData?.frameColor ?? []).map(transformFrame))
  const getFrameColors = computed(() => groupBy(
    getFrameItems.value.filter(item => !item.value.includes('pattern')),
    item => item.description,
  ))
  const getFramePattern = computed(() => getFrameItems.value.filter(item => item.value.includes('pattern')))
  const getFrameFiltered = computed(() => {
    return Object.keys(getFrameColors.value).map((key) => {
      const lastItem = [...getFrameColors.value[key]].shift()
      return {
        key,
        label: key,
        renderColor: lastItem.renderColor,
      }
    })
  })
  const getTempleColors = computed(() => {
    return state.masterData.templeColor?.map(transformTemple) ?? []
  })
  const getLogoOptions = computed(() => toArray(logo.logoOptions))
  const getLensOptions = computed(() => toArray(len.lenOptions).map(transformLensOption))
  const getLensTypes = computed(() => toArray(len.lenTypes))
  const getLensColors = computed(() => {
    return state.masterData.lensColor?.map(transformLensColor) ?? []
  })
  const getLensGrouping = computed(() => groupBy(
    getLensColors.value.filter(item => item.description),
    item => item.description,
  ))
  const getLensClear = computed(() => getLensColors.value.filter(item => !item.description))
  const getLensFiltered = computed(() => {
    return Object.keys(getLensGrouping.value).map((key) => {
      const groupObject = getLensGrouping.value[key]
      const firstItem = [...groupObject].pop()
      const meta = len.lenFunctionIntro[key]

      if (firstItem?.filtered?.includes('normal')) {
        return {
          key,
          type: firstItem.filtered,
          renderColor: firstItem.renderColor,
          photo: firstItem.photo,
          ...(meta && meta),
        }
      }

      const lastItem = [...groupObject].shift() // .split('_')
      return {
        key,
        type: lastItem.filtered,
        renderColor: lastItem.renderColor,
        photo: lastItem.photo,
        ...(meta && meta),
      }
    })
  })

  const getBridgeData = computed(() => state.masterData.bridgeName)
  const getBridgeOptions = computed(() => bridge.bridgeOptions)

  const getDefaultVariant = computed<ConfigurationType>(() => {
    const firstModel = getAllModels.value?.[0]
    const firstFrame = getFrameItems.value?.[0]
    const firstTemple = getTempleColors.value?.[0]
    const firstLen = getLensColors.value?.[0]

    const model = find(getAllModels.value, { value: state.defaultVariant?.modelName })
    const frame = find(getFrameItems.value, { value: state.defaultVariant?.frameColor })
    const temple = find(getTempleColors.value, { value: state.defaultVariant?.templeColor })
    const len = find(getLensColors.value, { value: state.defaultVariant?.lensColor })
    const bridge = find(getBridgeData.value, { value: state.defaultVariant?.bridgeName })

    return {
      model: model || firstModel,
      frame: frame || firstFrame,
      temple: temple || firstTemple,
      len: len || firstLen,
      bridge,
      lensOptions: state.defaultVariant?.lensOptions,
      logoFrontColor: state.defaultVariant?.logoFrontColor,
      logoBackColor: state.defaultVariant?.logoBackColor,
      logoFrontInfo: state.masterData?.logoFrontColor ?? [],
      logoBackInfo: state.masterData?.logoBackColor ?? [],
      lensOptionsInfo: state.masterData?.lensOptions ?? [],
      bridgeName: state.defaultVariant?.bridgeName,
    }
  })

  const getCurrentDemoMode = computed(() => state.demoMode)
  const getCurrentSwitchMode = computed(() => state.isShowSwitchMode)
  const getCurrentForceRefresh = computed(() => state.isForceRefresh)

  // TODO: No.186 Specification
  const filteredRandomModelSample = computed(() => {
    const data = getAllModels.value.filter(item => randomConfig.target.models.includes(item.value))
    return shuffle(data)
  })
  const filteredRandomFrameColorsSample = computed(() => {
    const data = getFrameItems.value.filter(item => randomConfig.target.frameColors.includes(item.value)).filter(item => !state.previousRandomKeys.includes(item.value))
    return shuffle(data)
  })
  const filteredRandomTempleColorsSample = computed(() => {
    const data = getTempleColors.value.filter(item => randomConfig.target.templeColors.includes(item.value)).filter(item => !state.previousRandomKeys.includes(item.value))
    return shuffle(data)
  })
  const filteredRandomLenColorsSample = computed(() => {
    const data = getLensColors.value.filter(item => randomConfig.target.lenColors.includes(item.value)).filter(item => !state.previousRandomKeys.includes(item.value))
    return shuffle(data)
  })

  const registerRandomPick = computed(() => {
    const addPreviousUnion = (value) => {
      Object.assign(state, {
        previousRandomKeys: state.previousRandomKeys.concat(value),
      })
    }

    const pickRandomModel = () => {
      /* eslint-disable @typescript-eslint/no-use-before-define */
      // TODO: It's mean reset union list
      Object.assign(state, { previousRandomKeys: [] })
      const sampleData = sample(filteredRandomModelSample.value)
      setModelType(sampleData)
      return sampleData
    }

    const pickRandomFrame = () => {
      const sampleData = sample(filteredRandomFrameColorsSample.value)
      addPreviousUnion(sampleData.value)
      console.info('Frame Color: ', sampleData.value)
      return sampleData
    }

    const pickRandomLens = () => {
      const sampleData = sample(filteredRandomLenColorsSample.value)
      addPreviousUnion(sampleData.value)
      console.info('Len Color: ', sampleData.value)
      return sampleData
    }

    const pickRandomTemple = () => {
      const sampleData = sample(filteredRandomTempleColorsSample.value)
      addPreviousUnion(sampleData.value)
      console.info('Temple Color: ', sampleData.value)
      console.info('======================')

      return sampleData
    }

    return new Map([
      ['model', pickRandomModel],
      ['frame', pickRandomFrame],
      ['lens', pickRandomLens],
      ['temple', pickRandomTemple],
      ['bridge', () => getBridgeData.value.find(item => item.value === 'bridge_2')],
      ['logoFrontColor', () => 'clear'],
      ['logoBackColor', () => 'white'],
    ])
  })
  // TODO: END No.186 =======

  // ? Actions
  const setModelType = (data: ModelNameType) => {
    const type = data?.value?.split('_')?.shift()?.toLocaleUpperCase()
    type && (state.currentNav = type)
  }

  const setCustomization = (payload) => {
    state.masterData = payload
  }

  const setInitialVariant = (payload: InputVariantType) => {
    const type = payload?.modelName?.split('_')?.shift()?.toLocaleUpperCase()
    if (type) {
      Object.assign(state, {
        currentNav: type,
        defaultVariant: payload,
      })
    }
  }

  const setInitialDemoMode = (value: boolean) => {
    state.demoMode = value
  }

  const setStatusSwitchMode = (value: boolean) => {
    state.isShowSwitchMode = value
  }

  const setStatusForceRefresh = (value: boolean) => {
    state.isForceRefresh = value
  }

  const getRandomByPart = (key = '') => {
    const func = registerRandomPick.value.get(key)
    return func?.()
  }

  // * Provide computed
  provide('getInputVariant', toRef(state, 'defaultVariant'))
  provide('getDefaultVariant', getDefaultVariant)
  provide('getCurrentModel', toRef(state, 'currentNav'))
  provide('getNavConfigurations', getNavConfigurations)
  provide('getModelOptions', getModelOptions)
  provide('getAllModels', getDisplayModels)
  provide('getFrameOptions', getFrameOptions)
  provide('getFrameColors', getFrameColors)
  provide('getFramePattern', getFramePattern)
  provide('getFrameFiltered', getFrameFiltered)
  provide('getTempleColors', getTempleColors)
  provide('getLogoOptions', getLogoOptions)
  provide('getLensOptions', getLensOptions)
  provide('getLensTypes', getLensTypes)
  provide('getLensColors', getLensColors)
  provide('getLensClear', getLensClear)
  provide('getBridgeOptions', getBridgeOptions)
  provide('getBridgeData', getBridgeData)
  provide('getLensGrouping', getLensGrouping)
  provide('getLensFiltered', getLensFiltered)
  provide('getCurrentDemoMode', getCurrentDemoMode)
  provide('getCurrentSwitchMode', getCurrentSwitchMode)
  provide('getCurrentForceRefresh', getCurrentForceRefresh)
  // * Provide method
  provide('setModelType', setModelType)
  provide('setCustomization', setCustomization)
  provide('setInitialVariant', setInitialVariant)
  provide('setInitialDemoMode', setInitialDemoMode)
  provide('setStatusSwitchMode', setStatusSwitchMode)
  provide('setStatusForceRefresh', setStatusForceRefresh)
  provide('getRandomByPart', getRandomByPart)
}

export const useCustomization = () => ({
  getInputVariant: inject('getInputVariant') as ComputedRef<InputVariantType>,
  getDefaultVariant: inject('getDefaultVariant') as ComputedRef<ConfigurationType>,
  getCurrentModel: inject('getCurrentModel') as ComputedRef<AnyObject>,
  getNavConfigurations: inject('getNavConfigurations') as ComputedRef<AnyObject>,
  getModelOptions: inject('getModelOptions') as ComputedRef<AnyObject>,
  getAllModels: inject('getAllModels') as ComputedRef<ModelNameType[]>,
  getFrameOptions: inject('getFrameOptions') as ComputedRef<AnyObject>,
  getFrameFiltered: inject('getFrameFiltered') as ComputedRef<AnyObject>,
  getFrameColors: inject('getFrameColors') as ComputedRef<FrameColorType[]>,
  getFramePattern: inject('getFramePattern') as ComputedRef<FrameColorType[]>,
  getTempleColors: inject('getTempleColors') as ComputedRef<TempleColorType[]>,
  getLogoOptions: inject('getLogoOptions') as ComputedRef<AnyObject[]>,
  getLensOptions: inject('getLensOptions') as ComputedRef<AnyObject>,
  getLensTypes: inject('getLensTypes') as ComputedRef<AnyObject>,
  getLensColors: inject('getLensColors') as ComputedRef<LensColorType[]>,
  getLensClear: inject('getLensClear') as ComputedRef<LensColorType[]>,
  getLensGrouping: inject('getLensGrouping') as ComputedRef<AnyObject>,
  getLensFiltered: inject('getLensFiltered') as ComputedRef<AnyObject>,
  getBridgeOptions: inject('getBridgeOptions') as ComputedRef<BridgeType[]>,
  getBridgeData: inject('getBridgeData') as ComputedRef<BridgeNameType[]>,
  getCurrentDemoMode: inject('getCurrentDemoMode') as ComputedRef<boolean>,
  getCurrentSwitchMode: inject('getCurrentSwitchMode') as ComputedRef<boolean>,
  getCurrentForceRefresh: inject('getCurrentForceRefresh') as ComputedRef<boolean>,
  // TODO: ===================== Inject function ==================
  setModelType: inject('setModelType') as (params) => void,
  setCustomization: inject('setCustomization') as (value) => void,
  setInitialVariant: inject('setInitialVariant') as (value) => void,
  setInitialDemoMode: inject('setInitialDemoMode') as (value) => void,
  setStatusSwitchMode: inject('setStatusSwitchMode') as (value) => void,
  setStatusForceRefresh: inject('setStatusForceRefresh') as (value) => void,
  getRandomByPart: inject('getRandomByPart') as (value) => void,
})
